import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import ConfirmMessageModal from "components/ConfirmMessageModal";
import { renderWhatsAppFormatting } from "lib/whatsappFormatting";
import radioCheck from "assets/svg/radio-check.svg";
import { formatTmNoForDisplay } from "utils/tmNumberDisplay";

import {
  OppositionWorkflowStep,
  getOppositionApplicationProgress,
  getOppositionCounterByOpponent,
  previewOppositionInformClientAboutCounter,
  sendOppositionInformClientAboutCounter,
  type CounterByOpponentItem,
  type OppositionApplicationProgressEntry,
} from "api/opposition";

export const OPPOSITION_INFORM_CLIENT_ABOUT_COUNTER_FORM_ID =
  "opposition-inform-client-about-counter-form";

type GroupKey = "yes" | "no";

type GroupState = {
  marks: CounterByOpponentItem[];
  preview: string | null;
  previewError: string | null;
  previewLoading: boolean;
  sent: boolean;
  sending: boolean;
};

function emptyGroup(marks: CounterByOpponentItem[]): GroupState {
  return {
    marks,
    preview: null,
    previewError: null,
    previewLoading: false,
    sent: false,
    sending: false,
  };
}

function RadioCircle({ active }: { active: boolean }) {
  if (active) return <img src={radioCheck} alt="" width={14} height={14} />;
  return (
    <span
      aria-hidden
      className="inline-block w-[14px] h-[14px] rounded-full border border-[#C3C3C3] bg-white"
    />
  );
}

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
  onSentStatusChange?: (sent: boolean) => void;
};

export default function OppositionInformClientAboutCounterView({
  leadId,
  onSuccess,
  onSentStatusChange,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [yesGroup, setYesGroup] = useState<GroupState>(emptyGroup([]));
  const [noGroup, setNoGroup] = useState<GroupState>(emptyGroup([]));
  const [phoneNumber, setPhoneNumber] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmGroup, setConfirmGroup] = useState<GroupKey | null>(null);
  const [confirmMode, setConfirmMode] = useState<"send" | "resend">("send");

  const hasYes = yesGroup.marks.length > 0;
  const hasNo = noGroup.marks.length > 0;
  const yesDone = !hasYes || yesGroup.sent;
  const noDone = !hasNo || noGroup.sent;
  // Step is "complete" when every eligible group has been sent. When neither
  // group exists yet (Step 1 not saved), keep parent's "Send and Continue"
  // button visible by reporting `false`.
  const allEligibleSent = (hasYes || hasNo) && yesDone && noDone;

  useEffect(() => {
    onSentStatusChange?.(allEligibleSent);
  }, [allEligibleSent, onSentStatusChange]);

  // --- Initial load: counter data + per-application progress ---
  useEffect(() => {
    if (!leadId) {
      setYesGroup(emptyGroup([]));
      setNoGroup(emptyGroup([]));
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getOppositionCounterByOpponent(leadId),
      getOppositionApplicationProgress(leadId).catch(
        () => [] as OppositionApplicationProgressEntry[]
      ),
    ])
      .then(([data, appProgress]) => {
        if (cancelled) return;
        const yes = data.conflictingMarks.filter((m) => m.counterFiled === true);
        const no = data.conflictingMarks.filter((m) => m.counterFiled === false);

        const completedByMark = new Map<string, boolean>();
        for (const entry of appProgress) {
          const step = entry.steps.find(
            (s) => s.slug === OppositionWorkflowStep.OP_S3_03_InformClientAboutCounter
          );
          completedByMark.set(entry.conflictingMarkId, Boolean(step?.isCompleted));
        }

        setYesGroup({
          ...emptyGroup(yes),
          sent: yes.length > 0 && yes.every((m) => completedByMark.get(m.id) === true),
        });
        setNoGroup({
          ...emptyGroup(no),
          sent: no.length > 0 && no.every((m) => completedByMark.get(m.id) === true),
        });
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(
            err instanceof Error ? err.message : "Failed to load step data"
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId]);

  // --- Preview fetcher (per group) ---
  const loadPreview = useCallback(
    async (key: GroupKey, marks: CounterByOpponentItem[]) => {
      if (!leadId || marks.length === 0) return;
      const setGroup = key === "yes" ? setYesGroup : setNoGroup;
      setGroup((g) => ({ ...g, previewLoading: true, previewError: null }));
      try {
        const res = await previewOppositionInformClientAboutCounter(leadId, {
          conflictingMarkIds: marks.map((m) => m.id),
        });
        setGroup((g) => ({
          ...g,
          preview: res.message,
          previewLoading: false,
        }));
        setPhoneNumber((prev) => prev || res.recipientPhone);
      } catch (err) {
        setGroup((g) => ({
          ...g,
          preview: null,
          previewError:
            err instanceof Error ? err.message : "Failed to load preview",
          previewLoading: false,
        }));
      }
    },
    [leadId]
  );

  useEffect(() => {
    if (hasYes && yesGroup.preview === null && !yesGroup.previewLoading) {
      loadPreview("yes", yesGroup.marks);
    }
  }, [hasYes, yesGroup.marks, yesGroup.preview, yesGroup.previewLoading, loadPreview]);

  useEffect(() => {
    if (hasNo && noGroup.preview === null && !noGroup.previewLoading) {
      loadPreview("no", noGroup.marks);
    }
  }, [hasNo, noGroup.marks, noGroup.preview, noGroup.previewLoading, loadPreview]);

  // --- Send flow ---
  const triggerSend = (key: GroupKey, mode: "send" | "resend") => {
    if (!leadId) return;
    const group = key === "yes" ? yesGroup : noGroup;
    if (group.sending) return;
    if (group.previewError || !group.preview) {
      toast.error(
        group.previewError ??
          "Complete the previous step to record YES/NO before sending the message."
      );
      return;
    }
    setConfirmGroup(key);
    setConfirmMode(mode);
    setShowConfirm(true);
  };

  const handleConfirmProceed = () => {
    if (!leadId || !confirmGroup) {
      setShowConfirm(false);
      return;
    }
    const key = confirmGroup;
    const group = key === "yes" ? yesGroup : noGroup;
    const setGroup = key === "yes" ? setYesGroup : setNoGroup;
    setShowConfirm(false);
    setGroup((g) => ({ ...g, sending: true }));
    (async () => {
      try {
        await sendOppositionInformClientAboutCounter(leadId, {
          conflictingMarkIds: group.marks.map((m) => m.id),
        });
        setGroup((g) => ({ ...g, sent: true, sending: false }));
        toast.success(
          confirmMode === "resend"
            ? "Message resent to client successfully."
            : "Message sent to client successfully."
        );
      } catch (err) {
        setGroup((g) => ({ ...g, sending: false }));
        toast.error(err instanceof Error ? err.message : "Failed to send");
      } finally {
        setConfirmGroup(null);
      }
    })();
  };

  // --- Bottom-bar form submit (mirrors existing pattern):
  // - All eligible groups sent → navigate to next step.
  // - Else → trigger send for the first pending group (YES preferred when both
  //   pending so the positive flow runs first). User clicks again for the
  //   remaining group if applicable.
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId) return;
    if (allEligibleSent) {
      onSuccess?.(leadId);
      return;
    }
    if (hasYes && !yesGroup.sent) {
      triggerSend("yes", "send");
      return;
    }
    if (hasNo && !noGroup.sent) {
      triggerSend("no", "send");
      return;
    }
  };

  if (!leadId) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12">
        <p className="text-sm text-[#6D6D6D]">Loading…</p>
      </div>
    );
  }

  if (!hasYes && !hasNo) {
    return (
      <form
        id={OPPOSITION_INFORM_CLIENT_ABOUT_COUNTER_FORM_ID}
        onSubmit={handleSubmit}
        className="flex flex-col gap-[22px] w-full max-w-[1085px]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Record YES/NO for Counter by Conflicting Proprietor in the previous
          step before sending the message.
        </p>
      </form>
    );
  }

  return (
    <form
      id={OPPOSITION_INFORM_CLIENT_ABOUT_COUNTER_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-8 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {hasYes && (
        <GroupSection
          title="Counter Filed (YES)"
          subtitle="Applications where the applicant of the conflicting mark filed a Counter Statement. The Evidence-filing template is sent to the client."
          group={yesGroup}
          phoneNumber={phoneNumber}
          onSend={() => triggerSend("yes", "send")}
          onResend={() => triggerSend("yes", "resend")}
        />
      )}
      {hasNo && (
        <GroupSection
          title="Counter Not Filed (NO)"
          subtitle="Applications where the opponent did not file a Counter Statement before the last date. The abandonment-pending template is sent to the client."
          group={noGroup}
          phoneNumber={phoneNumber}
          onSend={() => triggerSend("no", "send")}
          onResend={() => triggerSend("no", "resend")}
        />
      )}

      <ConfirmMessageModal
        open={showConfirm}
        onClose={() => {
          setShowConfirm(false);
          setConfirmGroup(null);
        }}
        previewContent={
          confirmGroup === "yes"
            ? yesGroup.preview ?? "—"
            : confirmGroup === "no"
              ? noGroup.preview ?? "—"
              : "—"
        }
        onConfirm={handleConfirmProceed}
        disableConfirm={
          !confirmGroup ||
          (confirmGroup === "yes" && (!yesGroup.preview || !!yesGroup.previewError)) ||
          (confirmGroup === "no" && (!noGroup.preview || !!noGroup.previewError))
        }
      />
    </form>
  );
}

type GroupSectionProps = {
  title: string;
  subtitle: string;
  group: GroupState;
  phoneNumber: string;
  onSend: () => void;
  onResend: () => void;
};

function GroupSection({
  title,
  subtitle,
  group,
  phoneNumber,
  onSend,
  onResend,
}: GroupSectionProps) {
  const canSend =
    !group.previewError && !!group.preview && group.marks.length > 0 && !group.sending;

  return (
    <section className="flex flex-col gap-[18px] rounded-[10px] border border-[#EEEEEE] bg-white p-5">
      <div className="flex flex-col gap-1">
        <h3 className="text-[14px] font-semibold leading-5 text-[#222]">{title}</h3>
        <p className="text-[12px] leading-[18px] text-[#6D6D6D]">{subtitle}</p>
      </div>

      {group.marks.length > 1 && (
        <div className="flex flex-row flex-wrap gap-3">
          {group.marks.map((m) => (
            <div
              key={m.id}
              className="flex items-center gap-2 px-2.5 py-2.5 rounded-[6px] h-10 min-w-[140px] bg-[#222222] text-white"
              style={{ fontSize: "0.75rem" }}
            >
              <RadioCircle active />
              {formatTmNoForDisplay(m.applicationNo ?? "")}
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-3">
        <p className="text-[14px] leading-5 text-[#222]">Message Preview</p>
        <div className="rounded-[6px] border border-[#EEEEEE] bg-white p-[14px] min-h-[148px]">
          {group.previewLoading && !group.preview ? (
            <p className="font-sans text-[12px] leading-5 text-[#6D6D6D] m-0">
              Loading preview…
            </p>
          ) : group.preview ? (
            <pre className="font-sans text-[12px] leading-5 text-[#222] m-0 whitespace-pre-wrap break-words">
              {renderWhatsAppFormatting(group.preview)}
            </pre>
          ) : (
            <p className="font-sans text-[12px] leading-5 text-[#6D6D6D] m-0">
              {group.previewError ?? "Preview unavailable."}
            </p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <input
          type="tel"
          inputMode="numeric"
          value={phoneNumber}
          readOnly
          placeholder="e.g. +919876543210"
          className="w-full min-w-0 sm:w-[200px] h-9 px-[10px] py-2 text-[12px] text-[#222] border border-[#EEEEEE] rounded-[6px] bg-[#F7F7F7] placeholder:text-[#6D6D6D]"
        />
        {group.sent ? (
          <button
            type="button"
            disabled={!canSend}
            onClick={onResend}
            className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-[#2E3037] bg-white border border-[#D5D3E0] rounded-[10px] whitespace-nowrap hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {group.sending ? "Sending…" : "Resend Message"}
          </button>
        ) : (
          <button
            type="button"
            disabled={!canSend}
            onClick={onSend}
            className="inline-flex items-center justify-center h-9 px-4 text-[12px] font-medium leading-5 text-white bg-[#222222] border border-[#222222] rounded-[10px] whitespace-nowrap hover:bg-[#000] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {group.sending ? "Sending…" : "Send Message"}
          </button>
        )}
        {group.sent && (
          <span className="text-[12px] text-[#1F7F3D] font-medium">
            Message sent to client
          </span>
        )}
      </div>
    </section>
  );
}
